import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  IconButton,
  CircularProgress,
  Checkbox,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { TextInput } from "../../../../components/TextInput";
import { CDTAndCustomProcedureValue } from "../../../../components/CDTAndCustomProcedureSelect";
import {
  CheckIcon,
  CrossIcon,
  PlusIcon,
  TrashIcon,
  CalendarIcon,
  RightArrowIcon,
  MoveAppointmentIcon,
} from "../../../../components/Icons";
import {
  EditableAppointmentData,
  EditableProcedureData,
} from "../../../../types/editableTreatmentPlan";
import {
  getOptionSelectValue,
  OPTION_NUMBERS,
} from "../../../../utils/conditionalPathUtils";
import {
  EditAppointmentContainer,
  EditAppointmentHeader,
  EditAppointmentFee,
  EditAppointmentHeaderActions,
  EditAppointmentDescriptionField,
  EditAppointmentDescriptionInput,
  EditProcedureRow,
  EditProcedureNameCell,
  EditProcedureDragHandle,
  EditProcedureNameInput,
  EditAddStepRow,
  EditAppointmentTimeframeRow,
  EditTimeframeBar,
  EditTimeframeInput,
  EditTimeframeArrow,
  EditAppointmentMetaFields,
  EditOptionContainer,
  EditOptionLabel,
  EditOptionSelect,
  EditDurationField,
  EditDurationInput,
  EditDurationSuffix,
} from "./EditTreatmentAppointment.styled";
import {
  AppointmentNameCell,
  AppointmentNumber,
} from "../TreatmentAppointment/TreatmentAppointment.styled";
import { colors } from "../../../../theme/colors";
import EditProcedureCdtField from "./EditProcedureCdtField";
import EditProcedureToothField from "./EditProcedureToothField";
import EditProcedureSurfaceField from "./EditProcedureSurfaceField";

export interface EditTreatmentAppointmentProps {
  appointmentId: number;
  appointmentNumber: number;
  isLastAppointment: boolean;
  draft: EditableAppointmentData;
  hasChanges: boolean;
  isSaving: boolean;
  onDraftAppointmentChange: (
    field: keyof EditableAppointmentData,
    value: unknown,
  ) => void;
  onDraftProcedureChange: (
    procedureIndex: number,
    field: keyof EditableProcedureData,
    value: unknown,
  ) => void;
  onDraftCdtChange: (
    procedureIndex: number,
    value: CDTAndCustomProcedureValue,
    allCdtCodes: string[],
    allDescriptions: string[],
    totalFee: number,
    totalDurationMinutes: number,
  ) => void;
  onSave: (durationMinutes: number) => void;
  onCancel: () => void;
}

const EditTreatmentAppointment: React.FC<EditTreatmentAppointmentProps> = ({
  appointmentId,
  appointmentNumber,
  isLastAppointment,
  draft,
  hasChanges,
  isSaving,
  onDraftAppointmentChange,
  onDraftProcedureChange,
  onDraftCdtChange,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();

  const isOptionActive = Boolean(draft.conditionalPath?.trim());
  const optionValue = getOptionSelectValue(draft.conditionalPath || "");
  const computedDuration = draft.procedures.reduce(
    (sum, procedure) => sum + (procedure.durationMinutes || 0),
    0,
  );
  const resolvedDuration = draft.isDurationEdited
    ? (draft.totalDurationMinutes ?? 0)
    : (draft.totalDurationMinutes ?? computedDuration);

  const [durationInput, setDurationInput] = useState(
    resolvedDuration === 0 ? "" : String(resolvedDuration),
  );
  const [isDurationFocused, setIsDurationFocused] = useState(false);
  const [draggingProcedureIndex, setDraggingProcedureIndex] = useState<
    number | null
  >(null);
  const [dragOverProcedureIndex, setDragOverProcedureIndex] = useState<
    number | null
  >(null);
  const dragProcedureRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isDurationFocused) {
      setDurationInput(resolvedDuration === 0 ? "" : String(resolvedDuration));
    }
  }, [resolvedDuration, isDurationFocused]);

  const totalFee = draft.procedures.reduce(
    (sum, procedure) => sum + (parseFloat(procedure.fee || "0") || 0),
    0,
  );

  const handleAddProcedure = () => {
    const newProcedure: EditableProcedureData = {
      text: "",
      description: "",
      sortOrder: draft.procedures.length + 1,
      cdtCodes: [],
      customProcedureId: undefined,
      resolvedCdtCodes: [],
      explanation: "",
      teeth: [],
      surfaces: [],
      durationMinutes: 20,
      fee: "$0",
      isFeeEdited: false,
    };
    onDraftAppointmentChange("procedures", [...draft.procedures, newProcedure]);
  };

  const handleDeleteProcedure = (procedureIndex: number) => {
    const updatedProcedures = draft.procedures
      .filter((_, idx) => idx !== procedureIndex)
      .map((item, idx) => ({
        ...item,
        sortOrder: idx + 1,
      }));
    onDraftAppointmentChange("procedures", updatedProcedures);
  };

  const handleProcedureReorder = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;

    const reordered = [...draft.procedures];
    const [dragged] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, dragged);

    onDraftAppointmentChange(
      "procedures",
      reordered.map((procedure, index) => ({
        ...procedure,
        sortOrder: index + 1,
      })),
    );
  };

  const handleProcedureDragStart = (
    event: React.DragEvent,
    procedureIndex: number,
  ) => {
    event.stopPropagation();
    dragProcedureRef.current = procedureIndex;
    setDraggingProcedureIndex(procedureIndex);
    event.dataTransfer.effectAllowed = "move";

    const dragHandle = event.currentTarget as HTMLElement;
    const procedureRow = dragHandle.closest(
      "[data-procedure-row]",
    ) as HTMLElement | null;
    if (procedureRow) {
      const rowRect = procedureRow.getBoundingClientRect();
      const offsetX = event.clientX - rowRect.left;
      const offsetY = event.clientY - rowRect.top;

      const dragImage = procedureRow.cloneNode(true) as HTMLElement;
      dragImage.style.width = `${rowRect.width}px`;
      dragImage.style.position = "absolute";
      dragImage.style.top = "-9999px";
      dragImage.style.left = "0";
      dragImage.style.opacity = "1";
      dragImage.style.pointerEvents = "none";
      document.body.appendChild(dragImage);

      event.dataTransfer.setDragImage(dragImage, offsetX, offsetY);
      requestAnimationFrame(() => {
        document.body.removeChild(dragImage);
      });
    }
  };

  const handleProcedureDragOver = (
    event: React.DragEvent,
    procedureIndex: number,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (
      dragProcedureRef.current !== null &&
      dragProcedureRef.current !== procedureIndex
    ) {
      setDragOverProcedureIndex(procedureIndex);
    }
  };

  const handleProcedureDragLeave = (event: React.DragEvent) => {
    event.stopPropagation();
    const relatedTarget = event.relatedTarget as Node | null;
    if (!event.currentTarget.contains(relatedTarget)) {
      setDragOverProcedureIndex(null);
    }
  };

  const handleProcedureDrop = (
    event: React.DragEvent,
    procedureIndex: number,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (dragProcedureRef.current !== null) {
      handleProcedureReorder(dragProcedureRef.current, procedureIndex);
    }
    dragProcedureRef.current = null;
    setDraggingProcedureIndex(null);
    setDragOverProcedureIndex(null);
  };

  const handleProcedureDragEnd = (event: React.DragEvent) => {
    event.stopPropagation();
    dragProcedureRef.current = null;
    setDraggingProcedureIndex(null);
    setDragOverProcedureIndex(null);
  };

  const handleFeeBlur = (procedureIndex: number, fee: string) => {
    const normalizedFee = fee.replace(/^\$/, "").trim();
    if (!normalizedFee) {
      onDraftProcedureChange(procedureIndex, "fee", "0");
    }
  };

  const handleOptionToggle = (checked: boolean) => {
    onDraftAppointmentChange(
      "conditionalPath",
      checked ? getOptionSelectValue(draft.conditionalPath || "") : "",
    );
  };

  const handleOptionNumberChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    onDraftAppointmentChange("conditionalPath", event.target.value);
  };

  const commitDuration = (digits: string) => {
    const value = digits === "" ? 0 : parseInt(digits, 10);
    onDraftAppointmentChange("totalDurationMinutes", value);
  };

  const handleDurationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/\D/g, "");
    setDurationInput(digits);
    commitDuration(digits);
  };

  const handleDurationBlur = () => {
    setIsDurationFocused(false);
    commitDuration(durationInput.replace(/\D/g, ""));
  };

  const getDurationMinutesFromInput = () => {
    const digits = durationInput.replace(/\D/g, "");
    return digits === "" ? 0 : parseInt(digits, 10);
  };

  const handleSaveClick = () => {
    const durationMinutes = getDurationMinutesFromInput();
    commitDuration(durationInput.replace(/\D/g, ""));
    onSave(durationMinutes);
  };

  return (
    <>
      <EditAppointmentContainer isSaving={isSaving}>
        <EditAppointmentHeader>
          <AppointmentNameCell>
            <EditAppointmentDescriptionField>
              <AppointmentNumber>#{appointmentNumber}</AppointmentNumber>
              <EditAppointmentDescriptionInput>
                <TextInput
                  height="38px"
                  value={draft.description}
                  onChange={(event) =>
                    onDraftAppointmentChange("description", event.target.value)
                  }
                />
              </EditAppointmentDescriptionInput>
            </EditAppointmentDescriptionField>
            <EditAppointmentMetaFields>
              <Checkbox
                size="small"
                checked={isOptionActive}
                onChange={(event) => handleOptionToggle(event.target.checked)}
                sx={{
                  padding: 0,
                  color: colors.primary.main,
                  "&.Mui-checked": {
                    color: colors.primary.main,
                  },
                }}
              />
              <EditOptionContainer>
                <EditOptionLabel>Opt</EditOptionLabel>
                {isOptionActive && (
                  <EditOptionSelect
                    value={optionValue}
                    onChange={handleOptionNumberChange}
                  >
                    {OPTION_NUMBERS.map((number) => (
                      <option key={number} value={number}>
                        {number}
                      </option>
                    ))}
                  </EditOptionSelect>
                )}
              </EditOptionContainer>
              <EditDurationField>
                <EditDurationInput
                  value={durationInput}
                  placeholder="0"
                  inputMode="numeric"
                  onChange={handleDurationChange}
                  onFocus={() => setIsDurationFocused(true)}
                  onBlur={handleDurationBlur}
                />
                <EditDurationSuffix>min</EditDurationSuffix>
              </EditDurationField>
            </EditAppointmentMetaFields>
          </AppointmentNameCell>
          <Box />
          <Box />
          <Box />
          <EditAppointmentFee>
            ${totalFee.toLocaleString("en-US")}
          </EditAppointmentFee>
          <EditAppointmentHeaderActions>
            <IconButton
              size="small"
              onClick={handleSaveClick}
              disabled={isSaving || !hasChanges}
            >
              {isSaving ? (
                <CircularProgress
                  size={20}
                  sx={{ color: colors.primary.main }}
                />
              ) : (
                <CheckIcon
                  width="24px"
                  height="24px"
                  fill={
                    !hasChanges ? colors.border.medium : colors.primary.main
                  }
                />
              )}
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={onCancel}
              disabled={isSaving}
            >
              <CrossIcon
                width="24px"
                height="24px"
                fill={colors.status.error}
              />
            </IconButton>
          </EditAppointmentHeaderActions>
        </EditAppointmentHeader>

        {draft.procedures.map((procedure, procedureIndex) => (
          <EditProcedureRow
            key={`${appointmentId}-${procedure.id ?? procedureIndex}`}
            data-procedure-row
            isDragging={draggingProcedureIndex === procedureIndex}
            isDragOver={dragOverProcedureIndex === procedureIndex}
            onDragOver={(event) =>
              handleProcedureDragOver(event, procedureIndex)
            }
            onDragLeave={handleProcedureDragLeave}
            onDrop={(event) => handleProcedureDrop(event, procedureIndex)}
            onDragEnd={handleProcedureDragEnd}
          >
            <EditProcedureNameCell>
              <EditProcedureDragHandle
                draggable
                onDragStart={(event) =>
                  handleProcedureDragStart(event, procedureIndex)
                }
              >
                <MoveAppointmentIcon width="18px" height="18px" />
              </EditProcedureDragHandle>
              <EditProcedureNameInput>
                <TextInput
                  height="38px"
                  fontSize="14px"
                  value={procedure.text}
                  onChange={(event) =>
                    onDraftProcedureChange(
                      procedureIndex,
                      "text",
                      event.target.value,
                    )
                  }
                />
              </EditProcedureNameInput>
            </EditProcedureNameCell>
            <EditProcedureCdtField
              appointmentId={appointmentId}
              procedureIndex={procedureIndex}
              procedure={procedure}
              onCdtChange={(
                value,
                allCdtCodes,
                allDescriptions,
                fee,
                totalDurationMinutes,
              ) =>
                onDraftCdtChange(
                  procedureIndex,
                  value,
                  allCdtCodes,
                  allDescriptions,
                  fee,
                  totalDurationMinutes,
                )
              }
            />
            <EditProcedureToothField
              appointmentId={appointmentId}
              procedureIndex={procedureIndex}
              procedure={procedure}
              onChange={(teeth) =>
                onDraftProcedureChange(procedureIndex, "teeth", teeth)
              }
            />
            <EditProcedureSurfaceField
              appointmentId={appointmentId}
              procedureIndex={procedureIndex}
              procedure={procedure}
              onChange={(surfaces) =>
                onDraftProcedureChange(procedureIndex, "surfaces", surfaces)
              }
            />
            <TextInput
              height="30px"
              fullWidth={false}
              autoWidth
              minWidth={60}
              fontSize="14px"
              padding="0 12px"
              maxLength={7}
              value={"$" + procedure.fee.replace(/^\$/, "")}
              onChange={(event) =>
                onDraftProcedureChange(
                  procedureIndex,
                  "fee",
                  event.target.value.replace(/^\$/, ""),
                )
              }
              onBlur={(event) =>
                handleFeeBlur(procedureIndex, event.target.value)
              }
            />
            <Box display="flex" justifyContent="flex-end">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDeleteProcedure(procedureIndex)}
              >
                <TrashIcon width="18px" height="18px" />
              </IconButton>
            </Box>
          </EditProcedureRow>
        ))}

        <EditAddStepRow>
          <Button
            size="extraSmall"
            variant="dashed"
            startIcon={
              <PlusIcon width="18px" height="18px" fill={colors.primary.main} />
            }
            onClick={handleAddProcedure}
          >
            {t("editTreatmentPlan.addStep")}
          </Button>
        </EditAddStepRow>

        {!isLastAppointment && (
          <EditAppointmentTimeframeRow>
            <EditTimeframeBar>
              <CalendarIcon
                width="20px"
                height="20px"
                fill={colors.text.white}
              />
              <EditTimeframeInput
                value={draft.timeframeValue}
                placeholder={t(
                  "editTreatmentPlan.timeUntilNextAppointmentPlaceholder",
                )}
                onChange={(event) =>
                  onDraftAppointmentChange("timeframeValue", event.target.value)
                }
              />
              <EditTimeframeArrow>
                <RightArrowIcon
                  width="20px"
                  height="20px"
                  fill={colors.text.white}
                />
              </EditTimeframeArrow>
            </EditTimeframeBar>
          </EditAppointmentTimeframeRow>
        )}
      </EditAppointmentContainer>
    </>
  );
};

export default EditTreatmentAppointment;
